import React from 'react';
import { toast } from 'react-toastify';
import moment from 'moment';
import { makeStyles, Grid, Typography, Tooltip, Icon } from '@material-ui/core';
import { Spacer } from '@hopdrive/storybook';

import { useWallet } from './useWallet';

import IconAction from '../../reusable/IconAction';

/** Capitalize the first letter of the first word */
const capFirst = (str = null) => {
  if (str && typeof str === `string`) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
  return str;
};

//////////////////////////////////////// COMPONENT ////////////////////////////////////////

export default function WalletBadge({ payoutMethod, onRemoved, onReplace }) {
  const cls = useStyles();

  const { removeBankAccount } = useWallet();

  const ending = payoutMethod.accountLast4 ? ` ending in ${payoutMethod.accountLast4}` : ``;

  const actions = [
    {
      label: `Replace Bank Account`,
      handler: () => {
        if (onReplace) onReplace();
      },
    },
    {
      label: `Remove Bank Account`,
      handler: async () => {
        const confirmed = window.confirm(
          `Remove the bank account${ending}? You will not be paid by direct deposit until you add another one.`
        );
        if (!confirmed) return;
        const res = await removeBankAccount();
        if (res && res.success) {
          toast.success(`Successfully removed bank account!`, { autoClose: 2500 });
          if (onRemoved) onRemoved(res.data);
        } else {
          toast.error(res.message || `Failed to remove bank account!`);
        }
      },
    },
  ];

  return (
    <div className={cls.wallet}>
      <Grid container spacing={2} alignItems='center'>
        <Grid item>
          <Icon className={cls.walletIco}>wallet</Icon>

          {payoutMethod.status === `verified` ? (
            <Tooltip title={`Bank account has been verified`}>
              <Icon className={cls.verifiedIco}>check_circle_outline</Icon>
            </Tooltip>
          ) : (
            <Tooltip title={`Bank account has NOT been verified yet`}>
              <Icon className={cls.unverifiedIco}>remove_circle_outline</Icon>
            </Tooltip>
          )}
        </Grid>
        <Grid item xs>
          <Typography className={cls.nameTxt}>{payoutMethod.accountName}</Typography>
          <Typography className={cls.bankNameTxt}>{payoutMethod.bankName}</Typography>

          <Spacer size='xs' />

          <Typography className={cls.subTxt}>
            {capFirst(payoutMethod.accountType) || `Checking`}
            {payoutMethod.accountLast4 ? ` ••••${payoutMethod.accountLast4}` : ``}
          </Typography>
          {payoutMethod.createdAt ? (
            <Typography className={cls.subTxt}>
              Added {moment(payoutMethod.createdAt).format(`MM/DD/YYYY`)} at{' '}
              {moment(payoutMethod.createdAt).format(`h:mm A`)}
            </Typography>
          ) : null}
        </Grid>
      </Grid>

      <div className={cls.gear}>
        <IconAction actions={actions} />
      </div>
    </div>
  );
}

//////////////////////////////////////// STYLES ////////////////////////////////////////

const useStyles = makeStyles(theme => ({
  wallet: {
    position: 'relative',
    minWidth: 240,
    maxWidth: 480,
    padding: theme.spacing(2),
    borderRadius: theme.shape.paperRadius,
    background: theme.palette.background.paper,
    boxShadow: theme.shadow.medium,
  },
  walletIco: {
    display: 'block',
    marginTop: -4,
    marginBottom: -4,
    fontSize: 48,
    color: theme.palette.text.secondary,
  },
  verifiedIco: {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    color: theme.palette.success.main,
  },
  unverifiedIco: {
    display: 'block',
    marginLeft: 'auto',
    marginRight: 'auto',
    color: theme.palette.text.disabled,
  },

  nameTxt: {
    lineHeight: 1.25,
    fontSize: 16,
    fontWeight: 600,
  },
  bankNameTxt: {
    lineHeight: 1.25,
    fontSize: 14,
    fontWeight: 400,
  },
  subTxt: {
    lineHeight: 1.25,
    fontSize: 12,
    fontWeight: 400,
  },

  gear: {
    position: 'absolute',
    top: 4,
    right: 4,
  },
}));
